import { useEffect, useState, useRef, type ComponentRef } from "react";
import { SearchSuggestionsResponse, getSearchSuggestions } from "./api";

import { HStack, Input, Button } from "@postenbring/hedwig-react";
import "./search.scss";

export function Search({
    headerData,
    trackingUrl,
}: {
    headerData: any;
    trackingUrl: string | null;
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchSuggestionsResponse, setSearchSuggestionsResponse] =
        useState<SearchSuggestionsResponse>();

    const searchInputRef = useRef<ComponentRef<typeof Input>>(null);

    // Use ref to store the current AbortController
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    useEffect(() => {
        const timeoutId = window.setTimeout(async () => {
            // Abort previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort({
                    reason: "New search term entered",
                });
            }

            if (searchTerm.length === 0) {
                setSearchSuggestionsResponse(undefined);
                return;
            }
            if (searchTerm.length < 3) {
                setSearchSuggestionsResponse(undefined);
                return;
            }

            // Create new AbortController for this request
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            const response = await getSearchSuggestions(
                headerData.searchApiUrl,
                searchTerm,
                abortController,
            );

            // Only update state if the request wasn't aborted
            if (!abortController.signal.aborted) {
                setSearchSuggestionsResponse(response);
            }
        }, 150);

        return () => window.clearTimeout(timeoutId);
    }, [searchTerm, headerData.searchApiUrl]);

    // Cleanup: abort any ongoing request when component unmounts
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort({
                    reason: "Component unmounted",
                });
            }
        };
    }, []);

    const isTrackingNumber = (text) => /^(?=.*[0-9])[a-z0-9]{5,35}$/.test(text);

    const getTrackingRedirectUrl = (trackingUrl: string, trackingNumber: string) => {
        const langQueryMatch = trackingUrl.match(/\?lang=[^&]*/);
        const langQuery = langQueryMatch?.[0];
        const baseTrackingUrl = langQuery ? trackingUrl.replace(langQuery, "") : trackingUrl;
        const normalizedTrackingUrl = baseTrackingUrl.endsWith("/")
            ? baseTrackingUrl
            : `${baseTrackingUrl}/`;

        return langQuery
            ? `${normalizedTrackingUrl}${trackingNumber}${langQuery}`
            : `${normalizedTrackingUrl}${trackingNumber}`;
    };

    const handleSearchButtonClick = () => {
        // Abort any ongoing request before navigating
        if (abortControllerRef.current) {
            abortControllerRef.current.abort({
                reason: "Navigating to search page",
            });
        }

        if (isTrackingNumber(searchTerm) && trackingUrl) {
            window.location.href = getTrackingRedirectUrl(trackingUrl, searchTerm);
        } else {
            const searchUrl = new URL(headerData.searchUrl, window.location.href);
            searchUrl.searchParams.set("q", searchTerm);
            window.location.href = searchUrl.toString();
        }
    };

    const localize = (key: string) => {
        return headerData.searchLocalization[key] || key;
    };

    return (
        <div className="kp-header-search-container">
            <HStack gap="4" align="end">
                <Input
                    label=""
                    role="search"
                    aria-label={headerData.searchButtonLabel}
                    placeholder={headerData.searchButtonLabel}
                    className="kp-header-search-input"
                    ref={searchInputRef}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleSearchButtonClick();
                        }
                    }}
                />
                <Button variant="primary" size="large" onClick={handleSearchButtonClick}>
                    {headerData.searchButtonLabel}
                </Button>
            </HStack>
            <ul className="kp-header-search-suggestions">
                {searchSuggestionsResponse?.hits.map((hit) => (
                    <li key={hit.serverUrl}>
                        <a className="kp-header-search-suggestion" href={hit.serverUrl}>
                            <h3 className="kp-header-search-suggestion-heading">{hit.heading}</h3>
                            <p className="kp-header-search-suggestion-category">
                                {localize(hit._category)}
                            </p>
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}
